<template>
  <div class="col-12" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card mb-5 " style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ $t('users.edit.title') }}</h6>
          </div>
        </div>
      </div>
   
      <LoadingSpinner v-if="isLoading && !user" :message="$t('users.edit.loading')" />

      <div v-else-if="user" class="card-body px-0 pb-2 mx-2 ">
        <div class="row justify-content-center">
          <div class="col-md-12">
            <UserForm 
              ref="userForm"
              :initial-data="initialUserData"
              :is-edit="true"
              @submit="handleSubmit" 
              @cancel="handleCancel" 
              @validation-error="handleValidationError"
              @update:form-data="handleFormUpdate"
            />
            <div class="d-flex gap-3 mt-3">
              <button 
                type="button" 
                class="btn btn-primary btn-sm flex-grow-1"
                @click="submitForm"
                :disabled="isSubmitting || !hasChanges"
              >
                <span v-if="isSubmitting">
                  <i class="fas fa-spinner fa-spin" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                  {{ $t('users.edit.saving') }}
                </span>
                <span v-else>
                  <i class="fas fa-edit" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                  {{ $t('users.edit.saveChanges') }}
                </span>
              </button>
              <button 
                type="button" 
                class="btn btn-outline-secondary btn-sm flex-grow-1"
                @click="handleCancel"
                :disabled="isSubmitting"
              >
                <i class="fas fa-times" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                {{ $t('users.edit.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="card-body px-0 pb-2 pe-3 text-center py-4">
        <div class="alert alert-danger">
          {{ $t('users.edit.notFound') }}
        </div>
        <button class="btn btn-secondary" @click="handleCancel">
          {{ $t('users.edit.backToList') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import UserForm from '@/components/features/user/forms/UserForm.vue';
import { useToast } from 'vue-toastification'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import { useI18n } from 'vue-i18n';
const { locale, t: $t } = useI18n();

const toast = useToast();
const router = useRouter();
const route = useRoute();
const store = useStore();
const userForm = ref(null);
const user = ref(null); 
const isLoading = ref(true); 
const isSubmitting = ref(false);
const avatarFile = ref(null);

const initialUserData = ref({ 
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  gender: 'male',
  role_id: null,
  phone_num: '',
  is_active: true,
  avatar_url: null
});

const originalUserData = ref({});
const currentFormData = ref({});

// Check if form has changes
const hasChanges = computed(() => {
  const origPerms = [...(originalUserData.value.permission_slugs || [])].sort();
 const currPerms = [...(currentFormData.value.permission_slugs || [])].sort();
  const permsChanged =
    JSON.stringify(origPerms) !== JSON.stringify(currPerms);

  return (
    currentFormData.value.name !== originalUserData.value.name ||
    currentFormData.value.email !== originalUserData.value.email ||
    currentFormData.value.gender !== originalUserData.value.gender ||
    currentFormData.value.role_id !== originalUserData.value.role_id ||
    currentFormData.value.phone_num !== originalUserData.value.phone_num ||
    currentFormData.value.is_active !== originalUserData.value.is_active ||
    (currentFormData.value.avatar_url !== originalUserData.value.avatar_url && 
     !avatarFile.value) ||
    avatarFile.value ||
    permsChanged
  );
});

// Handle form updates from child component
const handleFormUpdate = (newData) => {
  currentFormData.value = { ...newData };
  if (
    originalUserData.value &&
    originalUserData.value.permission_slugs === undefined &&
    Array.isArray(newData.permission_slugs)
  ) {
    originalUserData.value = {
      ...originalUserData.value,
      permission_slugs: [...newData.permission_slugs],
    };
  }
};

onMounted(async () => {
  try {
    const userId = route.params.id;
    const fetchedUser = await store.dispatch('fetchUser', userId);
    console.log('Fetched user data:', fetchedUser); 
    
    user.value = fetchedUser; 
    initialUserData.value = { 
      ...fetchedUser,
      role_id: fetchedUser.role?.id || null
    };
    originalUserData.value = { ...initialUserData.value };
    currentFormData.value = { ...initialUserData.value };
  } catch (error) {
    console.error('Error:', error);
    toast.error("فشل في تحميل البيانات");
  } finally {
    isLoading.value = false;
  }
});

const handleSubmit = async (formData) => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  
  try {
    const userId = route.params.id;
    
    let payload;
if (formData instanceof FormData) {
  formData.append('_method', 'PUT'); 
  payload = formData;               
} else {
  payload = {
    name: formData.name?.trim(),
    email: formData.email?.trim().toLowerCase(),
    gender: formData.gender,
    role_id: formData.role_id,
    phone_num: formData.phone_num,
    is_active: formData.is_active,
    avatar_url: formData.avatar_url,
    permission_slugs: formData.permission_slugs || [],   
  };
}
await store.dispatch('updateUser', { id: userId, data: payload });
    
  toast.success($t('users.edit.updateSuccess'));
    router.push('/users');
  } catch (error) {
    console.error('Update error:', error);
    if (error.response?.status === 422) {
      const errors = error.response.data.errors || {};
      userForm.value.setServerErrors(errors);
  toast.error($t('users.edit.updateError'));
    } else {
      toast.error(error.response?.data?.message || "فشل في تحديث بيانات المستخدم");
    }
  } finally {
    isSubmitting.value = false;
  }
};

const submitForm = () => {
  if (!hasChanges.value) {
    toast.info($t('users.edit.noChanges'));
    return;
  }
  userForm.value.submit();
};

const handleValidationError = () => {
  toast.warning($t('users.edit.validationError'))
};

const handleCancel = () => {
  router.push('/users');
};
</script>